import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target, Eye, Globe, MapPin, ArrowRight, CheckCircle,
  Wrench, HandCoins, Hammer, Home as HomeIcon, ShieldCheck, Network, Sparkles
} from 'lucide-react';
import { useT, RichText } from '../i18n/I18nContext';

const COMMUNITY_IMAGE = "https://images.unsplash.com/photo-1627423893729-3a79f48ff473?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHx2b2NhdGlvbmFsJTIwdHJhaW5pbmclMjB3b3Jrc2hvcCUyMGFmcmljYSUyMHN0dWRlbnRzfGVufDB8fHx8MTc3Mjc5ODQ4MXww&ixlib=rb-4.1.0&q=85";

const strategyIcons = [Wrench, HandCoins, Hammer, HomeIcon, ShieldCheck, Network, Sparkles];

export default function AboutPage() {
  const { t } = useT();
  const services = t('about.services.items') || [];
  const partners = t('about.partners.list') || [];
  // Reuse the same 7 strategy points from home.smart.points
  const strategyPoints = (t('home.smart.points') || []).map((p, i) => ({ ...p, Icon: strategyIcons[i] || Sparkles }));

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-primary" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('about.hero.eyebrow')}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 font-heading">
              {t('about.hero.title')}
            </h1>
            <p className="text-xl text-blue-100">{t('about.hero.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <RichText text={t('about.intro.p1')} className="block text-lg text-slate-600 leading-relaxed mb-6" />
              <RichText text={t('about.intro.p2')} className="block text-lg text-slate-600 leading-relaxed mb-6" />
              <RichText text={t('about.intro.p3')} className="block text-lg text-slate-600 leading-relaxed" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src={COMMUNITY_IMAGE}
                alt="Community engagement"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-6 bg-secondary text-white p-6 rounded-xl shadow-lg max-w-[260px]">
                <p className="text-3xl font-bold font-heading">2022</p>
                <p className="text-sm text-green-100">{t('about.intro.establishedNote')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-accent" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              className="bg-white p-8 rounded-2xl shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4 font-heading">{t('about.mission.title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('about.mission.body')}</p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-2xl shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center mb-6">
                <Eye className="h-7 w-7 text-secondary" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4 font-heading">{t('about.vision.title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('about.vision.body')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategy — 7 principles */}
      <section className="py-16 md:py-24 bg-white" data-testid="strategy-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading">{t('about.strategyTitle')}</h2>
            <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">{t('about.strategySubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {strategyPoints.map((point, i) => (
              <motion.div
                key={point.title}
                className="bg-accent rounded-2xl p-5 border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                data-testid={`strategy-point-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-primary text-white flex items-center justify-center mb-3">
                  <point.Icon className="h-5 w-5" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-1 font-heading text-base">{point.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 md:py-24 bg-accent" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('about.services.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 font-heading">
              {t('about.services.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={service}
                className="flex items-center gap-3 p-4 bg-white rounded-xl"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                <span className="text-slate-700 font-medium">{service}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16 md:py-24 bg-primary" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('about.partners.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 font-heading">
              {t('about.partners.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Globe className="h-12 w-12 text-secondary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2 font-heading">{partner.name}</h3>
                <p className="text-blue-100">{partner.location}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-16 md:py-24 bg-white" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('about.locations.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 font-heading">
              {t('about.locations.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              className="bg-accent p-8 rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 font-heading">{t('about.locations.hq')}</h3>
                  <p className="text-slate-600 whitespace-pre-line">{t('about.locations.hqAddr')}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-accent p-8 rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 font-heading">{t('about.locations.satellite')}</h3>
                  <p className="text-slate-600 whitespace-pre-line">{t('about.locations.satelliteAddr')}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Map Embed */}
          <div className="mt-12 rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127357.01246887396!2d9.124833!3d4.0234926!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1061137e5f0e0e1b%3A0x4c7f4d0eb4c56e4a!2sLimbe%2C%20Cameroon!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SMART Centre Cameroon Location"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
            {t('about.cta.title')}
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            {t('about.cta.body')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-secondary hover:bg-green-50 rounded-full px-8 py-4 font-semibold text-lg transition-all shadow-lg"
            data-testid="about-cta"
          >
            {t('about.cta.button')} <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
